/**
 * Selected countries as removable chips, next to a card title (and under the
 * tab filter). A fully-selected PlayStation region shows as one chip (SIEA,
 * SIEE, SIEAsia, SIEJ); a partly-selected one shows its countries. "All" shows
 * an ALL COUNTRIES chip plus the four region chips, so removing a region from
 * "all" is one click.
 */

import { X } from 'lucide-react';
import { REGION_INFO, groupCountryOptions, type CountryOption } from '@/utils/countryRegions';

interface Chip {
  key: string;
  label: string;
  /** region chips keep their case (SIEAsia); countries and "All" are upper-cased */
  region?: boolean;
  title?: string;
  /** codes this chip stands for (removed together); none = not removable */
  codes?: string[];
}

export function CountryChips({ options, selected, onChange, isDark, max = 6, vertical = false }: {
  options: CountryOption[];
  /** [] = All */
  selected: string[];
  onChange: (codes: string[]) => void;
  isDark: boolean;
  /** chips shown before "+N" */
  max?: number;
  /** narrow container (floating card): wrap freely */
  vertical?: boolean;
}) {
  if (!options.length) return null;
  const allCodes = options.map(o => o.code);
  const isAll = selected.length === 0;
  const sel = new Set(isAll ? allCodes : selected);
  const groups = groupCountryOptions(options);

  const chips: Chip[] = [];
  if (isAll) chips.push({ key: '__all', label: 'All countries', title: 'Every country is included' });
  for (const g of groups) {
    const codes = g.options.map(o => o.code);
    const on = codes.filter(c => sel.has(c));
    if (!on.length || (isAll && g.region === 'Non-SIE')) continue;
    if (on.length === codes.length) {
      chips.push({ key: g.region, label: g.region, region: true, title: `${g.region}: ${REGION_INFO[g.region]} (${codes.length} countries)`, codes });
    } else {
      for (const o of g.options) {
        if (sel.has(o.code)) chips.push({ key: o.code, label: o.name, title: `${o.name} (${g.region})`, codes: [o.code] });
      }
    }
  }

  const remove = (codes: string[]) => {
    const drop = new Set(codes);
    const next = allCodes.filter(c => sel.has(c) && !drop.has(c));
    onChange(next.length === allCodes.length ? [] : next);   // removing everything returns to All
  };

  const shown = chips.slice(0, max);
  const hidden = chips.length - shown.length;
  const chipCls = `relative inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold tracking-wide whitespace-nowrap ${
    isDark ? 'border-blue-400/60 text-gray-100 bg-gray-800/60' : 'border-blue-500/60 text-gray-800 bg-white'}`;

  return (
    <div className={`flex items-center gap-x-2 gap-y-2 ${vertical ? 'flex-wrap' : 'flex-wrap min-w-0'}`}>
      {shown.map(c => (
        <span key={c.key} className={`${chipCls} ${c.codes ? 'pr-2.5' : ''}`} title={c.title}>
          <span className={`max-w-[9rem] truncate ${c.region ? '' : 'uppercase'}`}>{c.label}</span>
          {c.codes && (
            <button type="button" aria-label={`Remove ${c.label}`} onClick={() => remove(c.codes!)}
              className={`absolute -top-1.5 -right-1.5 rounded-full p-[1px] ${
                isDark ? 'bg-gray-900 text-gray-300 hover:text-white' : 'bg-white text-gray-500 hover:text-gray-900'}`}>
              <X className="h-2.5 w-2.5" />
            </button>
          )}
        </span>
      ))}
      {hidden > 0 && (
        <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          title={chips.slice(max).map(c => c.label).join(', ')}>+{hidden} more</span>
      )}
    </div>
  );
}
